import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { faExchange } from '@fortawesome/pro-solid-svg-icons';
import {
  Title,
  Form,
  FormBody,
  FormGroup,
  Label,
  LabelText,
  Button,
  ClearInput,
  Box,
  LoadingOr,
  IconButton,
  InputMessage,
  themeUtils as tu,
} from '@zen-common/components-base';
import { assetUtils, textUtils } from '@zen/common-utils';
import { useUniqueId } from '@zen/common-react-hooks';

const { getAssetFullId } = assetUtils;

const ERROR_MSG = {
  ASSET_ID: 'Asset ID is not valid',
  BASE_QUOTE_SAME: 'Base and quote must be different',
};

/**
 * Order book search edit mode
 */
export default function EditMode({
  editBase,
  editQuote,
  setBase,
  setQuote,
  onSearch,
  searching,
  naming,
}) {
  const instanceId = useUniqueId();
  const baseQuoteSame =
    editBase.value !== '' && getAssetFullId(editBase.value) === getAssetFullId(editQuote.value);
  const searchDisabled = !editBase.valid || !editQuote.valid || baseQuoteSame || searching;
  const baseErrorMsg =
    editBase.valid === false ? ERROR_MSG.ASSET_ID : baseQuoteSame ? ERROR_MSG.BASE_QUOTE_SAME : '';
  const quoteErrorMsg =
    editQuote.valid === false ? ERROR_MSG.ASSET_ID : baseQuoteSame ? ERROR_MSG.BASE_QUOTE_SAME : '';

  const baseValid = editBase.valid !== undefined ? editBase.valid && !baseQuoteSame : undefined;
  const quoteValid = editQuote.valid !== undefined ? editQuote.valid && !baseQuoteSame : undefined;

  const handleBaseChangedOrPasted = (e) => {
    // allow pasting a pair in the base input in the form of xxx/yyy or xxx yyy
    const values = e.target.value.trim().split(/[\s/]+/);
    setBase(textUtils.filterHash(values[0]));
    if (values.length === 2) {
      setQuote(textUtils.filterHash(values[1]));
    }
  };
  const handleQuoteChangedOrPasted = (e) => {
    // allow pasting a pair in the quote input in the form of xxx/yyy or xxx yyy
    const values = e.target.value.trim().split(/[\s/]+/);
    if (values.length === 2) {
      setBase(textUtils.filterHash(values[0]));
      setQuote(textUtils.filterHash(values[1]));
    } else {
      setQuote(textUtils.filterHash(values[0]));
    }
  };
  const handleSwitchBaseQuote = () => {
    setBase(editQuote.value);
    setQuote(editBase.value);
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    !searchDisabled && onSearch(e);
  };
  const handleReset = (e) => {
    e.preventDefault();
    setBase('');
    setQuote('');
  };

  const preventSeparator = (e) => {
    if (/([^a-zA-Z0-9])+/g.test(e.key)) {
      e.preventDefault();
    }
  };

  const title = (asset) => (naming && naming[asset] ? `Asset - ${naming[asset].name}` : 'Asset');
  return (
    <>
      <Title mb="md">Search pairs</Title>
      <Form onSubmit={handleSubmit} disabled={searching}>
        <FormBody inline>
          <InputFormGroup mr={[0, 4, 0]}>
            <Label htmlFor={instanceId + 'base'}>
              <LabelText>Enter base {title(editBase.value)}</LabelText>
            </Label>
            <ClearInput
              id={instanceId + 'base'}
              value={editBase.value}
              valid={baseValid}
              width="276px"
              variant="block"
              onKeyDown={preventSeparator}
              onChange={handleBaseChangedOrPasted}
              onClear={() => setBase('')}
              disabled={searching}
            />
            <InputMessage>{baseErrorMsg}</InputMessage>
          </InputFormGroup>
          <SwitchButtonWrapperDesktop>
            <SwitchButton
              onClick={handleSwitchBaseQuote}
              tabIndex={-1}
              data-testid="EditMode-switch-button"
            />
          </SwitchButtonWrapperDesktop>
          <InputFormGroup mr={[0, 4]}>
            <Label htmlFor={instanceId + 'quote'}>
              <LabelText>Enter quote {title(editQuote.value)}</LabelText>
            </Label>
            <ClearInput
              id={instanceId + 'quote'}
              value={editQuote.value}
              valid={quoteValid}
              width="276px"
              variant="block"
              onKeyDown={preventSeparator}
              onChange={handleQuoteChangedOrPasted}
              onClear={() => setQuote('')}
              disabled={searching}
            />
            <InputMessage>{quoteErrorMsg}</InputMessage>
          </InputFormGroup>
          <ButtonsFormGroup width={['100%', 'auto']}>
            <Box display="flex" flexDirection={['row', 'row-reverse']} width={['100%', 'auto']}>
              <Button
                onClick={handleReset}
                disabled={!editBase.value && !editQuote.value}
                bg="secondary"
                width={['100%', '90px']}
              >
                Reset
              </Button>
              <SwitchButtonWrapperMobile>
                <SwitchButton onClick={handleSwitchBaseQuote} tabIndex={-1} />
              </SwitchButtonWrapperMobile>
              <Button
                width={['100%', '90px']}
                type="submit"
                disabled={searchDisabled}
                mr={['0', null, 'sm']}
              >
                <LoadingOr loading={searching}>Search</LoadingOr>
              </Button>
            </Box>
            <ErrorHeightPlaceholder />
          </ButtonsFormGroup>
        </FormBody>
      </Form>
    </>
  );
}
EditMode.propTypes = {
  editBase: PropTypes.shape({ value: PropTypes.string, valid: PropTypes.bool }),
  editQuote: PropTypes.shape({ value: PropTypes.string, valid: PropTypes.bool }),
  setBase: PropTypes.func,
  setQuote: PropTypes.func,
  onSearch: PropTypes.func,
  searching: PropTypes.bool,
};

const InputFormGroup = styled(FormGroup)`
  flex-grow: 1;
  max-width: ${tu.breakpoints.md};
  ${tu.mq({
    width: ['100%', null, 'auto'],
  })}
`;
const ButtonsFormGroup = styled(FormGroup)`
  align-self: flex-end;
`;
const SwitchButtonWrapperDesktop = styled(Box)`
  position: relative;
  top: -4px;
  ${tu.mq({
    display: ['none', null, 'block'],
  })}
`;
const SwitchButtonWrapperMobile = styled(Box)`
  position: relative;
  top: 2px;
  ${tu.mq({
    marginLeft: [tu.space(2), null, 0],
    marginRight: [tu.space(2), null, 0],
    display: ['block', null, 'none'],
  })}
`;

const SwitchButton = styled(IconButton).attrs({
  fontSize: 'xl',
  icon: faExchange,
  type: 'button',
})``;

const ErrorHeightPlaceholder = () => (
  <Box display={['none', 'block']}>
    <InputMessage />
  </Box>
);
